import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import NumberUtils from 'js/helpers/NumberUtils';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import useEvent from 'js/hooks/useEvent';
import DragHandle from 'js/components/Prescription/DragHandle/DragHandle';
import { PRESCRIPTION_UNIT } from '../../../../constants/PrescriptionConstants';
import {
  getIntervalDisplayBounds,
  SQUARE_METER_COVERSION_VALUE,
  getEffectiveSoilValues,
} from '../../PrescriptionUtils';
import { isSoilLayer } from '../../PrescriptionJob';
import { isNullInterval } from '../../nullValueRegions';

const HorizontalHandle = ({
  index,
  wrapperRect,
  interval,
  onDragValue,
  onDragEnd,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
}) => {
  const { prescriptionJob } = usePrescriptionJob();
  const { maxPrescription, fieldSize, intervals } = prescriptionJob;
  let centerValue = maxPrescription / fieldSize;
  if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
    centerValue = maxPrescription / (fieldSize * SQUARE_METER_COVERSION_VALUE);
  }

  const maxValue = centerValue * 2;

  const currentPrescription = interval.prescription;

  const [mouseDown, setMouseDown] = useState(false);

  const onMousemove = useCallback(
    (event) => {
      if (!wrapperRect || !mouseDown || !onDragValue) {
        return;
      }

      if (typeof event.persist === 'function') {
        event.persist();
      }

      const wrapperMax = wrapperRect.top;
      const wrapperMin = wrapperRect.bottom;
      let value = NumberUtils.map(event.clientY, wrapperMin, wrapperMax, 0, maxValue);
      value = NumberUtils.limit(value, 0, maxValue);
      if (!Number.isNaN(value)) {
        onDragValue(index, value);
      }
    },
    [wrapperRect, mouseDown, onDragValue, centerValue]
  );

  useEffect(() => {
    if (mouseDown) {
      const handleMousemove = (event) => onMousemove(event);

      window.addEventListener('mousemove', handleMousemove);

      return () => window.removeEventListener('mousemove', handleMousemove);
    }
  }, [mouseDown, onMousemove]);

  const handleStartDrag = useCallback(() => {
    setMouseDown(true);
  }, []);

  useEvent(
    'mouseup',
    useCallback(() => {
      if (mouseDown && onDragEnd) {
        onDragEnd();
      }
      setMouseDown(false);
    }, [mouseDown, onDragEnd])
  );

  const movementPercent = NumberUtils.limitMap(currentPrescription, 0, maxValue, 0, 100);
  const soilLayer = isSoilLayer(prescriptionJob.layer);
  const isBulkMode = isBulkPrescription && !appliedInitialIntervals;
  const effectiveValues = useMemo(
    () =>
      soilLayer
        ? getEffectiveSoilValues(
            prescriptionJob.values,
            prescriptionJob.layer,
            isBulkMode,
            bulkPrescriptionJobs
          )
        : null,
    [soilLayer, prescriptionJob.values, prescriptionJob.layer, isBulkMode, bulkPrescriptionJobs]
  );
  const bounds = getIntervalDisplayBounds(intervals, index, effectiveValues);
  const left = bounds.left + bounds.width / 2;
  const width = isNullInterval(interval)
    ? Math.max(((wrapperRect.width * bounds.width) / 100) * 0.7, 24)
    : ((wrapperRect.width * bounds.width) / 100) * 0.7;

  return (
    <DragHandle
      width={width}
      axisPositionPercent={`${left}%`}
      movementDirection={'vertical'}
      movementPercent={`${100 - movementPercent}%`}
      onStartDrag={handleStartDrag}
    />
  );
};

HorizontalHandle.propTypes = {
  onDragValue: PropTypes.func,
  onDragEnd: PropTypes.func,
  index: PropTypes.number,
  interval: PropTypes.object,
  wrapperRect: PropTypes.any,
};

export default memo(HorizontalHandle);
